import React, { type FC, type ReactNode } from 'react'
import { Button, type ButtonColor } from '@mobi/component-library/Base/Buttons'
import { Modal as ModalOverlay } from '@mobi/component-library/Overlays/Modal'
import {
  AlertBoxActionsStyled,
  AlertBoxContentStyled,
  AlertBoxIconStyled,
  AlertBoxImageStyled,
} from './AlertBox.styled'
import type { ActionOrientation } from './types'

type Props = {
  /**
   * Whether the alert box is open
   */
  isOpen: boolean
  /**
   * The title within the alert box
   */
  title: string
  /**
   * The alert box text
   */
  text: ReactNode
  /**
   * The svg icon to display
   */
  icon?: ReactNode
  /**
   * The color of the confirm action
   */
  confirmColor?: ButtonColor
  /**
   * The text of the confirm action
   */
  confirmText?: string
  /**
   * The callback for the confirm action
   */
  onConfirm?: VoidFunction
  /**
   * The color of the cancel action
   */
  cancelColor?: ButtonColor
  /**
   * The text of the cancel action
   */
  cancelText?: string
  /**
   * The callback for the cancel action
   */
  onCancel?: VoidFunction
  /**
   * Which direction the actions are oriented
   *
   * These values align to flex directions. `vertical` maps to `column` and horizontal to `row`. Adding the `-reverse` suffix will make them `column-reverse` and `row-reverse` respectively.
   */
  actionOrientation?: ActionOrientation
  /**
   * Whether the confirm button's action is loading a query
   */
  isLoading?: boolean
  /**
   * Whether to align content to the left
   */
  alignLeft?: boolean
  /**
   * The image to display
   */
  imageCover?: ReactNode
  /**
   * The max width of the alert box
   */
  maxWidth?: string
}

export const AlertBox: FC<Props> = ({
  isOpen,
  title,
  text,
  icon,
  confirmColor = 'primary',
  confirmText,
  onConfirm,
  cancelColor = 'primary_destructive',
  cancelText,
  onCancel,
  isLoading = false,
  actionOrientation = 'vertical',
  alignLeft = false,
  imageCover,
  maxWidth,
}) => {
  return (
    <ModalOverlay
      isOpen={isOpen}
      dismissStatus='disallowed'
      modalStyle={maxWidth ? { maxWidth } : undefined}
    >
      {imageCover && <AlertBoxImageStyled>{imageCover}</AlertBoxImageStyled>}

      <AlertBoxContentStyled alignLeft={alignLeft}>
        {icon && <AlertBoxIconStyled alignLeft={alignLeft}>{icon}</AlertBoxIconStyled>}

        <header>
          <h1>{title}</h1>
        </header>

        <div>{text}</div>

        <AlertBoxActionsStyled orientation={actionOrientation}>
          {onConfirm && confirmText && (
            <Button
              color={confirmColor}
              onClick={onConfirm}
              isLoading={isLoading}
              disabled={isLoading}
              type='button'
            >
              {confirmText}
            </Button>
          )}

          {cancelText && onCancel && (
            <Button color={cancelColor} onClick={onCancel} disabled={isLoading} type='button'>
              {cancelText}
            </Button>
          )}
        </AlertBoxActionsStyled>
      </AlertBoxContentStyled>
    </ModalOverlay>
  )
}

AlertBox.displayName = 'AlertBox'
